import type { Asset } from "@/data/types";
import { Card, Fld, PBar, PaneHeader, Totals } from "./parts";

export default function PrototypingTab({ asset }: { asset: Asset }) {
  const closed = asset.phases.filter((p) => p.status === "completata").length;
  const running = asset.phases.filter((p) => p.status === "in corso").length;
  const avg = Math.round(
    asset.phases.reduce((s, p) => s + p.progress, 0) / asset.phases.length,
  );
  const carats = asset.stones.reduce(
    (s, st) => s + st.caratsEach * st.quantity,
    0,
  );
  const stoneCount = asset.stones.reduce((s, st) => s + st.quantity, 0);
  const suppliers = new Set(asset.stones.map((s) => s.supplier)).size;
  const certified = asset.stones.filter((s) => s.certificate !== "—").length;

  const phaseStatusClass = (status: string) => {
    if (status === "completata") return "st ok";
    if (status === "in corso") return "st run";
    return "st";
  };

  return (
    <section className="pane">
      <PaneHeader
        asset={asset}
        h2="Prototipia"
        sub={
          <>
            {asset.ref} · {asset.label.toUpperCase()} · COMMESSA{" "}
            {asset.ref.split("-").slice(1).join("/")} · REPARTO PROTOTIPIA
          </>
        }
      />

      <Card title="◱ Stato commessa" note={`aggiornato ${asset.date} · 14:20`}>
        <div className="fgrid">
          <Fld label="Fase corrente" value={asset.currentPhase} />
          <Fld
            label="Avanzamento ore"
            value={`${asset.progressPct}%`}
            tone={asset.progressPct > 100 ? "warn" : undefined}
          />
          <Fld label="Ore consuntivate" value={String(asset.hoursSpent)} />
          <Fld label="Ore preventivate" value={String(asset.hoursBudget)} />
          <Fld label="Prototipista" value={asset.prototypist} />
          <Fld label="Consegna prevista" value={asset.deliveryDate} />
        </div>
      </Card>

      <Card
        title="▦ Fasi di lavorazione"
        note={`${asset.phases.length} fasi · ${closed} completate · ${running} in corso`}
      >
        <div className="tw">
          <table className="pt">
            <thead>
              <tr>
                <th>Fase</th>
                <th>Descrizione</th>
                <th>Responsabile</th>
                <th>Inizio</th>
                <th>Fine</th>
                <th className="num">Ore</th>
                <th>% compl.</th>
                <th>Stato</th>
              </tr>
            </thead>
            <tbody>
              {[...asset.phases].reverse().map((p) => (
                <tr
                  key={p.code}
                  className={p.status === "in corso" ? "cur" : undefined}
                >
                  <td className="mono">{p.code}</td>
                  <td className="nm">{p.label}</td>
                  <td>{p.owner}</td>
                  <td className="mono">{p.start}</td>
                  <td className="mono">{p.end}</td>
                  <td className="num">{p.hours ?? "—"}</td>
                  <td>
                    <PBar value={p.progress} />
                  </td>
                  <td>
                    <span className={phaseStatusClass(p.status)}>
                      {p.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Totals
          items={[
            ["Ore consuntivate", String(asset.hoursSpent)],
            ["Ore preventivate", String(asset.hoursBudget)],
            [
              "Scostamento",
              `${asset.hoursSpent - asset.hoursBudget >= 0 ? "+" : ""}${asset.hoursSpent - asset.hoursBudget}`,
            ],
            ["Fasi chiuse", `${closed} / ${asset.phases.length}`],
            ["Avanzamento medio", `${avg}%`],
          ]}
        />
      </Card>

      {asset.stones.length > 0 && (
        <Card
          title="▦ Distinta pietre"
          note={`pavé · ${stoneCount} pietre · ${carats.toFixed(2)} ct`}
        >
          <div className="tw">
            <table className="pt">
              <thead>
                <tr>
                  <th>Posizione</th>
                  <th>Taglio</th>
                  <th className="num">Carati / pz</th>
                  <th className="num">Quantità</th>
                  <th className="num">Carati tot.</th>
                  <th>Fornitore</th>
                  <th>Certificato</th>
                </tr>
              </thead>
              <tbody>
                {asset.stones.map((s) => (
                  <tr key={s.position}>
                    <td className="nm">{s.position}</td>
                    <td>{s.cut}</td>
                    <td className="num">{s.caratsEach.toFixed(3)}</td>
                    <td className="num">{s.quantity}</td>
                    <td className="num">
                      {(s.caratsEach * s.quantity).toFixed(3)}
                    </td>
                    <td>{s.supplier}</td>
                    <td className="mono">{s.certificate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Totals
            items={[
              ["Pietre", String(stoneCount)],
              ["Carati totali", carats.toFixed(2)],
              ["Fornitori", String(suppliers)],
              ["Certificate", String(certified)],
            ]}
          />
        </Card>
      )}
    </section>
  );
}
